import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ARTICLES } from "@/mocks/articles";

const NAV = [
  { to: "/", label: "首页", end: true },
  { to: "/category/juzu", label: "剧组内幕" },
  { to: "/category/jingji", label: "经纪人手记" },
  { to: "/category/yenei", label: "业内观察" },
  { to: "/category/zhiqing", label: "知情者密档" },
  { to: "/about", label: "关于我们" },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const results = useMemo(() => {
    const kw = q.trim();
    if (!kw) return [];
    return ARTICLES.filter((a) =>
      `${a.title}${a.tags.join("")}${a.excerpt}`.includes(kw)
    ).slice(0, 6);
  }, [q]);

  const submitSearch = () => {
    const kw = q.trim();
    if (!kw) return;
    const hit = results[0];
    if (hit) {
      navigate(`/article/${hit.slug}`);
      setQ("");
      setSearchOpen(false);
      setOpen(false);
    }
  };

  return (
    <header>
      {/* 顶部快讯条 */}
      <div className="border-b border-background-200 bg-background-100">
        <div className="container-page flex items-center justify-between gap-4 py-2">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <span className="label-caps shrink-0 rounded-sm bg-primary-500 px-1.5 py-0.5 text-background-50">
              实时热瓜
            </span>
            <div className="relative min-w-0 flex-1 overflow-hidden">
              <p className="truncate text-xs text-foreground-700">
                {ARTICLES.filter((a) => a.hot).slice(0, 1).map((a) => (
                  <Link
                    key={a.slug}
                    to={`/article/${a.slug}`}
                    className="transition-colors hover:text-primary-600"
                  >
                    {a.title}
                  </Link>
                ))}
              </p>
            </div>
          </div>
          <p className="hidden shrink-0 items-center gap-1.5 text-xs text-foreground-500 sm:flex">
            <i className="ri-calendar-2-line text-primary-600"></i>
            每日更新 · 圈内信源持续入驻
          </p>
        </div>
      </div>

      {/* 主导航 */}
      <div
        className={`sticky top-0 z-40 w-full transition-colors duration-300 ${
          scrolled
            ? "border-b border-background-200 bg-background-50/95 backdrop-blur"
            : "bg-background-50"
        }`}
      >
        <div className="container-page flex items-center justify-between gap-4 py-3">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-lg text-background-50">
              <i className="ri-fire-fill"></i>
            </span>
            <span className="leading-tight">
              <span className="block font-heading text-lg font-extrabold tracking-tight text-foreground-950">
                明星八卦圈内
              </span>
              <span className="block text-[11px] text-foreground-500">
                Staff · Agent · Insider
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `relative rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "text-primary-600"
                      : "text-foreground-700 hover:text-primary-600"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    {isActive && (
                      <span className="absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full bg-primary-500" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              aria-label="站内搜索"
              className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-600 transition-colors hover:bg-background-200 hover:text-primary-600"
            >
              <i className="ri-search-line text-lg"></i>
            </button>
            <Link
              to="/about#contact"
              className="c-btn hidden bg-primary-500 px-4 text-background-50 hover:bg-primary-600 sm:inline-flex"
            >
              <i className="ri-quill-pen-line mr-1.5"></i>
              投稿爆料
            </Link>
            <button
              type="button"
              aria-label="打开菜单"
              onClick={() => setOpen((v) => !v)}
              className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-200 lg:hidden"
            >
              <i className={`${open ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
            </button>
          </div>
        </div>

        {/* 搜索面板 */}
        {searchOpen && (
          <div className="container-page pb-3">
            <div ref={boxRef} className="relative">
              <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2.5 focus-within:border-primary-300">
                <i className="ri-search-line text-foreground-500"></i>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onFocus={() => setFocused(true)}
                  onKeyDown={(e) => e.key === "Enter" && submitSearch()}
                  placeholder="搜索八卦、关键词、圈内栏目…"
                  className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
                />
                <button
                  type="button"
                  onClick={submitSearch}
                  className="whitespace-nowrap rounded-md bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50 hover:bg-primary-600"
                >
                  搜索
                </button>
              </div>
              {focused && results.length > 0 && (
                <ul className="absolute inset-x-0 top-full z-50 mt-1 overflow-hidden rounded-md border border-background-200 bg-background-50 shadow-lg">
                  {results.map((a) => (
                    <li key={a.slug}>
                      <Link
                        to={`/article/${a.slug}`}
                        onClick={() => {
                          setQ("");
                          setSearchOpen(false);
                          setOpen(false);
                        }}
                        className="flex items-center gap-3 border-b border-background-100 px-3 py-2.5 text-sm transition-colors hover:bg-background-100"
                      >
                        <i className="ri-article-line text-primary-500"></i>
                        <span className="flex-1 truncate text-foreground-800">{a.title}</span>
                        <span className="shrink-0 text-xs text-foreground-400">
                          {a.readTime}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              {focused && results.length === 0 && q.trim() && (
                <p className="absolute inset-x-0 top-full z-50 mt-1 rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-500">
                  没有找到相关内容，换个关键词试试
                </p>
              )}
            </div>
          </div>
        )}

        {/* 移动端菜单 */}
        {open && (
          <div className="border-t border-background-200 bg-background-50 lg:hidden">
            <nav className="container-page flex flex-col gap-1 py-3">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center justify-between rounded-md px-3 py-3 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-primary-50 text-primary-600"
                        : "text-foreground-800 hover:bg-background-100"
                    }`
                  }
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line"></i>
                </NavLink>
              ))}
              <Link
                to="/about#contact"
                onClick={() => setOpen(false)}
                className="mt-1 flex items-center justify-center gap-2 rounded-md bg-primary-500 px-3 py-3 text-sm font-semibold text-background-50"
              >
                <i className="ri-quill-pen-line"></i>
                我要投稿爆料
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}